import { CaseIncludedField } from 'app/area/case-report/case-listing/types';
import { CodeFieldIncludeInactive, CodeFieldUserCode } from 'app/area/codetable/types-store';
import { Consent } from 'app/area/user-profile/my-notifications/types';
import { QueryObject } from 'app/components/enhancers/with-router';
import { CodetableWireFormat } from 'app/infrastructure/ui/form/types-codetable';
import { Pathname, Search } from 'history';

/**
 * This file should only contain simple shared types with no external dependencies
 * DO NOT ADD imports TO THIS FILE, since that will easily create circular dependencies.
 */

/**
 * Supported view render modes
 * Must be defined here due to TS circ-refs
 */
export enum RenderMode {
  read = 0,
  edit = 1,
  report = 2,
  embeddedReport = 3
}

/**
 * A single text entry in a text collection
 *
 * @export
 * @interface Text
 */
export interface Text {
  id: TextId;
  description: string;
}

/**
 * Application texts are retreived per logical area as TextCollections
 *
 * @export
 * @class TextCollection
 */
export class TextCollection {
  public id: number;
  public description: string;
  public texts: Text[];
}

export interface Entity {
  id: CompositeKeys;
  operation?: EntityOperations;
  primaryKey?: ComplexValue<number>;
  [field: string]: StoreField[] | ChildEntityReference | CompositeKeys | EntityOperations | ComplexValue<number> | Record<string, ComplexValue<number>[]>;
}

export interface StoreItemPath {
  [entityType: string]: number;
}

/**
 * CompositeKeys attempts to describe the legacy database compound keys as Primary Key for a Case
 */
export type CompositeKeys = StoreItemPath[];

export type EntityOperations = 'Remove' | undefined;

export interface ComplexValue<T extends ValueProperty> {
  readonly value: T;
  readonly description?: string;
  readonly longDescription?: string;
  readonly icon?: string;
  readonly backgroundColor?: number;
  readonly foregroundColor?: number;
  readonly descendants?: ComplexValue<T>[];
  readonly allLevelDescription?: string;
  readonly minLevel?: number;
  readonly title?: string;
  readonly categoryId?: number;
  readonly extra?: { [key: string]: number | string | boolean };
  readonly owner?: ComplexValue<T>;
  readonly sharedThroughUserGroup?: boolean;
  readonly disabled?: Date;
  readonly parentAllLevelsDescription?: string;
  bookmark?: boolean;
}

export interface StoreField {
  value: number | string;
  description?: string;
  categoryId?: number;
}

/**
 * Type for `value` property within a ComplexValue shape
 */
export type ValueProperty = string | number | boolean | Date; // Should we support list of selected date or bool?

export type ChildEntityReference = number[];

/**
 * Permissions settings for Case only, not draft documents
 */
export interface CasePermissions {
  caseId?: number;
  [indexer: string]: boolean | number;
}

/**
 * Change connection sequence for Case only, not draft documents
 * This is used to move connections up or down in the sequence
 */
export interface ChangeConnectionSequence {
  caseId?: number;
  responseWarning?: ResponseWarning[];
  fetching?: boolean;
}

/**
 * The Id for a text
 *
 * @export
 * @interface TextId
 */
export interface TextId {
  no: number;
  textSet: number;
}

/**
 * Code table value - JSON wire format
 */
export interface Value {
  v: any;
  d?: string;
  ld?: string;
  ald?: string;
  i?: string;
  f?: number;
  b?: number;
  c?: number;
  ctg?: number;
}

/**
 * Typed argument object to use when constructing a
 * code table API request
 */

export enum RequestApiMethod {
  post,
  get
}

export enum RequestRemoteApi {
  adminApi,
  servicesApi
}

export interface LoadOptions {
  domainId?: number;
  searchTerm?: string;
  sort?: 'asc' | 'desc';
  parentId?: number;
  siblingId?: number;
  levels?: number;
  query?: any;
  apiVersion?: string;
  categoryId?: number;
  displayNotSelected?: boolean;
  iteratorId?: number;
  ignoreCategoryFilters?: boolean;
  optionsCollectionId?: number;
  consequenceType?: number;
  includeNotSelected?: boolean;
  includeInactiveCodes?: CodeFieldIncludeInactive;
  excludeInactiveUsers?: CodeFieldIncludeInactive;
  excludeTechnicalUsers?: CodeFieldIncludeInactive;
  userCode?: CodeFieldUserCode;
  code?: { text: number | string };
  requestApiMethod?: RequestApiMethod;
  requestRemoteApi?: RequestRemoteApi;
  limitFieldsTo?: any[];
  indicatorSubstanceId?: number;
  substanceId?: number;
  leafNodesOnly?: boolean;
  includeDisabled?: boolean;
}

/*
 * This type can be used as a hint on action creators so that its 'dispatch' and 'getState' params are
 * correctly typed to match your store.
 */
export interface ActionDispatching<TAction, TState> {
  (dispatch: (action: TAction) => void, getState: () => TState): void;
}

export interface ApiResponse<T> {
  readonly data: T;
  readonly messages?: ServerMessages;
}

export interface ServerMessages {
  readonly userMessages?: any;
  readonly validationErrors?: any;
}

export interface HttpResponse<T> {
  readonly json: ApiResponse<T>;
  readonly status: number;
  readonly etag?: string;
  readonly expires?: string;
}

export interface BowtieSupported {
  caseId: number;
  supported: boolean;
}

/**
 * StoreData is the flattened array format that data is stored in, in the redux store
 *
 * This is mapped back and fort between the legacy wire format and the flat format in `normalize` module
 */
export type StoreData = Entity[];

/**
 * Domain specific metadata parsed from case metaData (readOnlyDomains, requiredFieldDomains)
 */
export type StoreDomainMeta = {
  [key: number]: {
    readOnly?: StoreDomainMatch;
    requiredField?: StoreDomainMatch;
    hidden?: StoreDomainMatch;
    validations?: ValidationDomainMatch[];
  };
};

/**
 * A domain can either match globally (for the whole case), or for specific entityPaths.
 * If globalMatch = true, it will match globally for the whole case. If globalMatch = false,
 * it only matches for sub parts of the case where the entity context matches one of the entityPaths.
 */
export interface StoreDomainMatch {
  globalMatch: boolean;
  entityPaths: CompositeKeys[];
}

export enum ValidationMessageLevel {
  Error = 1,
  Warning = 2
}

/**
 * A validation includes additional information like message, level and label
 */
export interface ValidationDomainMatch extends StoreDomainMatch {
  level: ValidationMessageLevel;
  label?: string;
  message?: string;
}

/**
 * Store entry point for metadata - contains information for store entities and global domains
 */
export interface StoreMetadata {
  domains?: StoreDomainMeta;
}

/**
 * Case data JSON formats
 */
export interface PrimaryKey {
  v: number;
  d?: string;
  ld?: string;
  ctg?: number;
  i?: string;
}

export interface EntityPath {
  tbl: string;
  pk: number;
}

export interface FieldValue {
  values: Value[];
}

export interface Status {
  currentStatus: Value;
  validNextStatuses?: Value[];
}

export interface Fields {
  [indexer: string]: FieldValue;
}

export interface SubTable {
  primaryKey: PrimaryKey;
  fields: Fields;
  subTables?: SubTables;
}

export interface SubTables {
  [indexer: string]: SubTable[];
}

export interface CaseData {
  primaryKey: PrimaryKey;
  caseType: Value;
  caseContext: Value;
  metaData?: Metadata;
  status: Status;
  fields: Fields;
  subTables?: SubTables;
  readonly etag?: string;
}

export interface DomainMetadata {
  domainId: number;
  dataTag?: string;
  entityPaths?: EntityPath[][];
}

export enum ConstraintResponseType {
  Error = 1,
  Warning = 2
}

export interface ResponseWarning {
   responseType?: ConstraintResponseType;
   responseText?: string;
   domainText?: string;
}

export interface FailedDomainConstraintEntityPath {
  entityType: string;
  entityId: number;
}

export interface FailedDomainConstraintMetaData {
  domainId: number;
  responseType: ConstraintResponseType;
  domainText?: string;
  message?: string;
  entityPaths?: FailedDomainConstraintEntityPath[][];
}

export interface Metadata {
  readOnlyDomains?: DomainMetadata[];
  requiredFieldDomains?: DomainMetadata[];
  requiredFieldDomainsForStatusChange?: DomainMetadata[];
  hiddenDomains?: DomainMetadata[];
  failedDomainConstraints?: FailedDomainConstraintMetaData[];
}

/**
 * Favourite definition
 */
export interface FavouriteDefinition {
  emailSubscribers?: string; //only for special reports
  favourite?: Value;
  selectedReport: Value;
  conditions: FavouriteCondition[];
  connections: FavouriteConnections[];
  exposureApplication?: Value;
  exposureFactor?: number;
  exposurePercent?: number;
  reports: ReportDefinition[];
  validState?: any;
  scheduleDefinitionId?: number;
  replaceDomainNodes?: FavouriteReplaceDomainNodes[];
}

export interface FavouriteDefinitionResponse<T> {
  data: T;
  messages: { userMessages: UserMessage[] };
}

export interface SelectionDefinition {
  id: number;
  domain: Value;
  levels?: number;
  interval?: number;
  period?: Value;
  type: Value;
  setLevelAccToCond?: boolean;
  fillEmpty?: number;
  selectedCode?: number;
  sortOrderType?: Value;
}

export interface ReportDefinition {
  report: Value;
  aggregationType: Value;
  targetApp?: Value;
  aggregationDomain: Value;
  specialReport?: SpecialReportDefinition; //only for special reports
  layoutType: Value;
  layoutAttributes: FavouriteLayoutAttributes[];
  uomSetDomain?: Value; //user of mesurement, not neccesary
  type: Value;
  sort?: any[];
  selections: SelectionDefinition[];
  options?: any[];
  excludedOptions?: any[];
  rollingAverage?: number;
  predefDate?: Date; //only for special reports,
  presentationType?: Value;
  dashboard?: Value; //only for dashboards
  drillDownReport?: Value;  //only for statistical reports
  includedFields: CaseIncludedField[]; //only for case reports
  drillDownLayoutType?: Value; //only for statistical reports
}

export interface SpecialReportDefinition extends Value {
  disabled?: string;
}

export interface FavouriteLayoutAttributes {
  layoutTypeId: number;
  layoutTypeContext: number;
  name: string;
  value: string | boolean;
}

/**
 * Favourite connections
 */
export interface FavouriteConnections {
  connected: Value;
  operator: Value;
}

/**
 * Favourite replace domain nodes
 */
export interface ReplaceDomainNodeDomains extends Value {
  pad?: string;
}

export interface DomainNodeValue extends Value {
  domains?: ReplaceDomainNodeDomains[];
}

export interface ReplaceDomainNodeValue extends Value {
  domain?: ReplaceDomainNodeDomains;
}

export interface FavouriteReplaceDomainNodes {
  domainNode: DomainNodeValue;
  replaceDomainNode?: ReplaceDomainNodeValue;
}

export interface FavouriteReplaceDomainNodesWire {
  replaceDomainNodes: FavouriteReplaceDomainNodes[];
}

/**
 * Favourite condition
 */
export interface FavouriteCondition {
  id?: number;
  domainDbFunction?: Value;
  domain?: Value;
  valueOperator?: Value;
  conditionOperator?: Value;
  conditionOperator2?: Value; // Probably has to be deleted
  extraSqlCondition?: string;
  exactLevel?: boolean;
  forceExists?: boolean;
  values?: FavouriteConditionValue[];
  advancedSearchType?: number;
  searchRequired?: boolean;
  required?: boolean;
}

/**
 * Favourite condition value
 */
export interface FavouriteConditionValue {
  id?: number;
  dbFunction?: Value;
  operator?: Value;
  periodPredef?: Value;
  code?: Value;
  number?: number;
  number2?: number;
  string?: string;
  date?: any;
  type?: string;
  myValue?: boolean;
}

/**
 * Favourite options
 */
interface DomainNode {
  description: string;
  value: number;
}

export interface DefaultSortOrder {
  domainNode: DomainNode;
  orderBySequence: number;
  sortOrderType: number;
}

export interface FavouriteOptions {
  report?: number;
  sort?: { [report: number]: DefaultSortOrder[] };
  conditions?: FavouriteCondition[];
  connections?: FavouriteConnections[];
  query?: string;
  predefDate?: string;
}

/**
 * Favourite result
 */
export interface FavouriteResult {
  metadata: any;
  results: any[];
}

export interface FavouriteLayoutModel {
  dashboardType: number;
  layoutType: number;
  layoutTypeContext: number;
  presentationType: number;
  layoutTypeAttributes: { [key: string]: string | boolean };
}

export interface AdvancedSearchTypes {
  id?: Value;
  conditionOperator?: Value;
  dbFunctionDomain?: Value;
  dbFunctionValue?: Value;
  dbFunctionValue2?: Value;
  dbFunctionValue3?: Value;
  valueOperator?: Value;
  valueOperator2?: Value;
  valueOperator3?: Value;
  dbFunctionDomain2?: Value;
  valueListOperator?: Value;
}

/**
 * Contextual information about the authenticated user
 *
 * @export
 * @interface UserInfo
 */
export interface UserInfo {
  id: number;
  userName: string;
  name: string;
  email: string;
  language: ComplexValue<number>;
  unit: ComplexValue<number>;
  location: ComplexValue<number>;
  userGroup: ComplexValue<number>;
  startupDashboardId: number;
  startupDashboardTypeId: number;
  access: AccessInfoData;
  showNotificationChannelsInfo: boolean;
  showSubscriptions: boolean;
  myProfileAllowChangingName: boolean;
  myProfileAllowChangingEmailAddress: boolean;
  userType: CodetableWireFormat;
}

/**
 * Email address of support
 *
 * @export
 * @interface SupportEmailAddress
 */

export interface SupportEmailAddress {
  data: SupportEmailAddressData;
}

export interface SupportEmailAddressData {
  emailAddress: string;
}

/**
 * Access info
 */
export interface AccessInfoData {
  adminPanel?: AdminInfoData;
  tools?: ToolsInfoData;
  surveyManager?: SurveyManagerAccessInfoData;
}

export interface AdminInfoData {
  user: AccessAdminInfoData;
  role: AccessAdminInfoData;
  codetable: AccessAdminInfoData;
  dashboard: AccessAdminInfoData;
  companyReport: CompanyReportAccessData;
  message: AccessAdminMessagesData;
  translation: AccessAdminInfoData;
  alternativeIdPanel: boolean;
  extraInfoPanel: boolean;
  restrictedAccessPanel: boolean;
  develop: boolean;
  experienceTransferAlertsPanel: boolean;
  favourite: AccessAdminInfoData;
  interfaceAdministration: AccessAdminInfoData;
  allowEmailAsDbUser: boolean;
  externalUrl: AccessAdminInfoData;
  subscriberGroup: AccessAdminInfoData;
  experienceTransfer: AccessAdminInfoData;
}

export interface AccessAdminInfoData {
  create: boolean;
  read: boolean;
  administration: boolean;
  administrationCentral: boolean;
  linkSubset: boolean;
  mfaRecovery?: boolean;
}

export interface CompanyReportAccessData extends AccessAdminInfoData {
  manageReportTemplates: boolean;
}

export interface AccessAdminMessagesData {
  read: boolean;
  edit: boolean;
  delete: boolean;
}

/**
 * Tools info
 *
 * @export
 * @interface ToolsInfo
 */

export interface ToolsInfo {
  messages: object;
  data: ToolsInfoData;
}

export interface ToolsInfoData {
  refreshCache: boolean;
  regenerateAggregatedData: boolean;
  excelAddIn: boolean;
  exportAndImport: ToolsInfoDataExportAndImport;
  notifications: boolean;
  manageSpecialReportTemplates: boolean;
}

export interface ToolsInfoDataExportAndImport {
  export: boolean;
  import: boolean;
}

/**
 * Global state of the authentication
 *
 * @export
 * @interface AuthState
 */
export enum AuthState {
  NotAuthenticated,
  Authenticated,
  Anonymous,
  AuthenticationFailed,
  LoggedOut
}

/**
 * The value representation passed as ...spread props to a UI-component
 */
export interface SelectedValue {
  readonly value: ValueProperty;
  readonly valueItem: ComplexValue<string> | ComplexValue<number>;
  readonly valueItems: (ComplexValue<string> | ComplexValue<number>)[];
}

/**
 *  Shape for a more readable mapping of the Code wire format
 */

export interface CurrentEditState {
  etag: string;
  hasChanges?: boolean;
  persistedValues: {};
  values: any[];
}

/**
 * The server configuration wire format
 *
 * UiDefinition
 *  Domain
 *    DomainAttributes
 *
 * @export
 * @interface UiDefinition
 */

/**
 * State required by most container components, shared across state slices
 */
export interface SharedState extends UserInfo {
  locale: string;
  authState: AuthState;
}

/**
 * Settings with origin on server, potentially cached locally in client storage
 */
export interface ClientConfig {
  fallbackLocale: string;
  utcOffset: number;
  baseUrl: string;
}

export interface TranslatedState {
  texts?: TextCollection;
  textsLanguage?: number;
  textsFetching?: boolean;
}

/**
 * Re-exports and rollup file to ease the job for configure-store to setup redux
 * Note that there is a Circular Dep between this file and areas
 *  to be able to strong-type the ApplicationState, which is rather cruicial
 */

export interface ShellState extends TranslatedState {
  settings: ClientConfig;
  userInfo?: UserInfo;
  authState: AuthState;
  fetching: boolean;
  fetchingAccessInfo?: boolean;
  errors: ErrorState[];
  messages: UserMessage[];
  version?: string;
  selectedModal?: SelectedModal;
  supportEmailAddress?: string;
  helpUrl?: string;
  consents?: Consent[];
  locale?: string;
  fetchingRefreshCache?: boolean;
  fetchingRegenerateAggregated?: boolean;
  fetchingLoadConsents?: boolean;
  isExternalUserHandlingPolicyAccepted?: boolean;
  messagesToUser?: MessagesToUserInfo[];
  updatingMessagesToUser?: boolean;
  fixedHeader?: boolean;
  manageSurveysUrl?: string;
  feedbackUrl?: string;
  instanceInfo?: InstanceInfo;
}

export interface MessagesToUserInfo {
  id: number;
  status: any;
  messageDate: Date;
  subject?: string;
  message?: string;
  updating?: boolean;
}

export interface ErrorState {
  message: string;
  error: any;
}

export interface UserMessage {
  id: number;
  title?: string;
  message: string;
  type: UserMessageType;
  link?: string;
}

export enum UserMessageType {
  success = 'success',
  error = 'error',
  info = 'info'
}

export interface SurveyManagerAccessInfoData {
  administration: boolean;
  read: boolean;
  send: boolean;
  edit: boolean;
}

export interface SurveyManagerSupportedData {
  caseId: number;
  supported: boolean;
}

export interface RiskEvaluationSupported {
  caseId: number;
  lossPotential: boolean;
  residualLossPotential: boolean;
}

export interface SelectedModal {
  name: string;
  endpoint?: string;
  description?: string;
}

export enum UserType {
  extensive = 2,
  basic = 3
}

export interface LocationDescriptorObject {
  pathname?: Pathname;
  search?: Search;
  query?: QueryObject;
  state?: any;
}

export interface InstanceInfo {
  tenantId: string;
  envType: string;
}
